import { requestJson } from "../api/apiClient";
import {
  getWeatherSourceDisplayName,
} from "../constants/colombiaWeatherSourceMetadata.js";

const BASE = "/api/admin/weather/colombia";

export async function fetchColombiaWeatherSourceHealth(filters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  return requestJson(`${BASE}/source-health${query ? `?${query}` : ""}`);
}

export async function fetchColombiaWeatherSourceDetail(sourceKey) {
  return requestJson(`${BASE}/sources/${encodeURIComponent(sourceKey)}`);
}

export async function checkColombiaWeatherSourceHealth(sourceKey) {
  return requestJson(`${BASE}/sources/${encodeURIComponent(sourceKey)}/check-health`, {
    method: "POST",
  });
}

export async function refreshColombiaRainViewerCache() {
  return requestJson(`${BASE}/rainviewer/refresh`, { method: "POST" });
}

export async function runColombiaIdeamDiscovery(sourceKey = "IDEAM_RADAR_COMPOSITE", body = {}) {
  return requestJson(
    `${BASE}/sources/${encodeURIComponent(sourceKey)}/ideam-discovery`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function catalogColombiaIdeamArchive(body = {}) {
  return requestJson(`${BASE}/ideam/archive/catalog`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function runColombiaSiataDiscovery(body = {}) {
  return requestJson(`${BASE}/siata/discovery`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function ingestColombiaSiataLayers(body = {}) {
  return requestJson(`${BASE}/siata/ingest`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function runColombiaAerocivilDiscovery(body = {}) {
  return requestJson(`${BASE}/aerocivil/discovery`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function runColombiaAerocivilCacheSync(body = {}) {
  return requestJson(`${BASE}/aerocivil/cache-sync`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export const HEALTH_STATUS_SEVERITY = {
  down: 1,
  stale: 2,
  warning: 3,
  experimental: 4,
  unknown: 5,
  no_data: 5,
  historical: 6,
  fresh: 7,
  disabled: 8,
};

export function compareHealthStatus(a, b) {
  const aSeverity = HEALTH_STATUS_SEVERITY[a] ?? HEALTH_STATUS_SEVERITY.unknown;
  const bSeverity = HEALTH_STATUS_SEVERITY[b] ?? HEALTH_STATUS_SEVERITY.unknown;
  return aSeverity - bSeverity;
}

export function filterWeatherSources(sources, filters) {
  let rows = [...(sources || [])];

  if (filters.status && filters.status !== "all") {
    rows = rows.filter((row) => row.healthStatus === filters.status);
  }

  if (filters.sourceType && filters.sourceType !== "all") {
    rows = rows.filter((row) => row.sourceType === filters.sourceType);
  }

  if (filters.officialFilter === "official") {
    rows = rows.filter((row) => row.isOfficial);
  } else if (filters.officialFilter === "third_party") {
    rows = rows.filter((row) => !row.isOfficial);
  }

  if (filters.liveFilter === "live") {
    rows = rows.filter((row) => row.isLive && !row.isHistorical);
  } else if (filters.liveFilter === "historical") {
    rows = rows.filter((row) => row.isHistorical);
  }

  if (filters.enabledOnly) {
    rows = rows.filter((row) => row.isEnabled);
  }

  if (filters.provider) {
    const provider = filters.provider.toLowerCase();
    rows = rows.filter((row) =>
      String(row.providerName || "").toLowerCase().includes(provider)
    );
  }

  if (filters.search) {
    const term = filters.search.toLowerCase();
    rows = rows.filter(
      (row) =>
        row.sourceKey.toLowerCase().includes(term) ||
        row.sourceName.toLowerCase().includes(term) ||
        String(row.providerName || "").toLowerCase().includes(term) ||
        String(getWeatherSourceDisplayName(row.sourceKey, "")).toLowerCase().includes(term)
    );
  }

  const sortBy = filters.sortBy || "statusSeverity";
  const sortDir = filters.sortDir === "asc" ? 1 : -1;

  rows.sort((a, b) => {
    if (sortBy === "sourceName") {
      return sortDir * a.sourceName.localeCompare(b.sourceName);
    }

    if (sortBy === "lastObservedDataAt") {
      const aTime = a.lastObservedDataAt ? new Date(a.lastObservedDataAt).getTime() : 0;
      const bTime = b.lastObservedDataAt ? new Date(b.lastObservedDataAt).getTime() : 0;
      return sortDir * (aTime - bTime);
    }

    if (sortBy === "freshnessMinutes") {
      const aVal = a.freshnessMinutes ?? Number.MAX_SAFE_INTEGER;
      const bVal = b.freshnessMinutes ?? Number.MAX_SAFE_INTEGER;
      return sortDir * (aVal - bVal);
    }

    return sortDir * compareHealthStatus(a.healthStatus, b.healthStatus);
  });

  return rows;
}

export {
  getSourceActions,
  getWeatherSourceActionMetadata,
  getWeatherSourceAdminActionsPlaceholder,
  getWeatherSourceAdminDescription,
  getWeatherSourceActionLabel,
  SOURCE_ACTIONS,
  WEATHER_SOURCE_ACTION_METADATA,
} from "./weatherSourceActionRegistry.js";

export {
  getWeatherSourceCategoryLabel,
  getWeatherSourceDisplayName,
  getWeatherSourceMetadata,
  isFallbackRadarSource,
  isHistoricalReplaySource,
  isLiveRadarSource,
  isPrimaryLiveColombiaRadarSource,
} from "../constants/colombiaWeatherSourceMetadata.js";
